import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import PropTypes from "prop-types"
import { useState, useCallback } from "react"
import { useForm } from "react-hook-form"
import { connect } from "react-redux"
import isEmail from "validator/lib/isEmail"
import * as Yup from "yup"

import api from "apis"
import Modal from "components/modal"
import { isTestMode, retry, jewlr_t } from "helpers/application"

const SendAHint = loadable(() => retry(() => import("./send-a-hint")))
const ShareCart = loadable(() => retry(() => import("./share-cart")))

const schema = Yup.object().shape({
  email: Yup.string().test(
    "test-email",
    "Please enter a valid email address",
    (value) => isEmail(value || "")
  ),
  name: Yup.string().required("Please provide your name."),
  recipient: Yup.string().required("Please provide their name."),
  recipient_address: Yup.string().test(
    "test-email",
    "Please enter a valid email address",
    (value) => isEmail(value || "")
  ),
})

const InternalShareProductsModal = ({ sendHintData, submitShareForm }) => {
  const [status, setStatus] = useState({})
  const [showContent, setShowContent] = useState(false)
  const shareHint = sendHintData.share_type === "product"

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      email: sendHintData.email_cookie || "",
      message: "",
      name: sendHintData.name_cookie || "",
      recipient: "",
      recipient_address: "",
      send_copy: false,
      send_email_to_friend: 1,
      subscribe: false,
      template: "no_theme",
      ts: sendHintData.timestamp || "",
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })
  const values = watch()

  const onSubmit = async (values) => {
    try {
      await submitShareForm(values, setStatus)
    } catch (err) {
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  const afterShow = useCallback(() => {
    setShowContent(true)
    setStatus({})
    reset()
  })

  return (
    <Modal
      afterShow={afterShow}
      contentStyle={{ pt: 0 }}
      headerStyle={{ mb: 0 }}
      height={shareHint ? "100%" : "fit-content"}
      label={
        shareHint
          ? "Send A Hint"
          : `Share Your ${jewlr_t(sendHintData.share_type_titleized) ?? ""}`
      }
      labelStyle={{ fontWeight: 500 }}
      // Hidden overflow-y to fix SendAHint Modal Header on top when scroll
      modalStyle={`
        overflow-y: hidden;
      `}
      show="#share-modal"
      variant="squircle"
      width={shareHint ? "auto" : "448px"}
    >
      {showContent && (
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            {errors?.root?.error && (
              <Typography color="error" mb={1}>
                {errors.root.error.message}
              </Typography>
            )}
            {shareHint ? (
              <SendAHint
                control={control}
                isSubmitting={isSubmitting}
                item={sendHintData.items[0]}
                setValue={setValue}
                status={status}
                values={values}
              />
            ) : (
              <ShareCart
                control={control}
                isSubmitting={isSubmitting}
                shareType={jewlr_t(sendHintData.share_type_titleized)}
                status={status}
              />
            )}
          </form>
        </div>
      )}
    </Modal>
  )
}

const mapStateToProps = (state) => {
  return {
    sendHintData: state.share.emailFormData,
  }
}

const mapDispatchToProps = (dispatch) => {
  return { dispatch }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { sendHintData } = stateProps
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    submitShareForm: async (values, setStatus) => {
      const data = {
        email: {
          email: values.email,
          friends_email: values.recipient_address,
          friends_name: values.recipient,
          message: values.message,
          name: values.name,
          template: values.template,
        },
        item_params: sendHintData.first_item_base64,
        send_copy: false,
        send_email_to_friend: 1,
        share_type: sendHintData.share_type,
        subscribe: false,
        ts: sendHintData.timestamp,
      }
      await api
        .patch(sendHintData.email_a_friend_products_path, data)
        .catch((error) => {
          console.log("submit share form error: ", error)
        })
        .then(() => {
          setStatus({
            success: `Successfully shared ${sendHintData.share_type}`,
          })

          // GA event
          if (!isTestMode) {
            if (sendHintData.share_type == "product") {
              if (typeof window.submit_ga_tracker !== "undefined") {
                window.submit_ga_tracker("/email_a_friend")
              }
              let event_properties = {
                "Style Code": sendHintData.items[0].style_code,
                line_item_details: sendHintData.items[0].product_options,
                recipient_email: data.email.friends_email,
                sender_email: data.email.email,
              }
              submit_ga_event_click(
                "Shares",
                "Product Email Share",
                sendHintData.items[0].style_code,
                undefined,
                event_properties
              )

              dataLayer.push({
                event: "sent-a-hint",
                styleCode: sendHintData.items[0].style_code,
              })
            } else {
              if (sendHintData.items) {
                sendHintData.items.forEach((item) => {
                  let event_properties = {
                    "Style Code": item.style_code,
                    line_item_details: item.details,
                    recipient_email: data.email.friends_email,
                    sender_email: data.email.email,
                  }
                  if (sendHintData.cart_type == "wishlist") {
                    event_properties["wishlist_token"] = sendHintData.cart_token
                  }

                  submit_ga_event_click(
                    "Shares",
                    sendHintData.share_type_titleized + " Item",
                    undefined,
                    undefined,
                    event_properties
                  )
                })
              }

              if (
                sendHintData.cart_type == "wishlist" ||
                sendHintData.share_type == "wishlist"
              ) {
                dataLayer.push({
                  event: "shared-wishlist",
                })
                submit_ga_event_click(
                  "Shares",
                  sendHintData.share_type_titleized,
                  undefined,
                  undefined,
                  { wishlist_token: sendHintData.cart_token }
                )
              } else {
                dataLayer.push({
                  event: "shared-cart",
                })
                submit_ga_event_click(
                  "Shares",
                  sendHintData.share_type_titleized
                )
              }
            }
          }
        })
    },
  }
}

InternalShareProductsModal.propTypes = {
  sendHintData: PropTypes.object,
  submitShareForm: PropTypes.func,
}

const ShareProductsModal = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalShareProductsModal)

export default ShareProductsModal
